import type { Locale } from "./i18n";

export const financingCopy = {
  en: {
    calculator: "Payment calculator",
    finance: "Finance",
    cash: "Cash",
    vehiclePrice: "Vehicle price",
    cashPrice: "Cash price",
    cashHelp: "Contact our team to confirm the final purchase total.",
    excludedFees: "Excludes taxes, registration and document fees.",
    downPayment: "Down payment",
    downPaymentSlider: "Down payment slider",
    downPaymentHelp: "$250 slider increments; you can enter an exact amount.",
    downPaymentError:
      "Enter a down payment from $0 to the vehicle price, with no more than two decimal places.",
    loanTerm: "Loan term",
    months: "months",
    creditTier: "Estimated credit score",
    creditHelp:
      "Choose a range to see your estimate. This does not check your credit.",
    credit: {
      excellent: "Excellent",
      very_good: "Very good",
      good: "Good",
      consultation: "Let’s talk",
    },
    lowScore: "619 or below",
    chooseCredit: "Choose your credit range to see an estimated payment.",
    loading: "Loading available rates…",
    manualReview: "Financing consultation",
    manualReviewHelp:
      "Leave your contact details below. Our team will discuss financing options with you.",
    ratesUnavailable: "An online rate is not available for this selection.",
    ratesUnavailableHelp:
      "You can still ask our team about financing for this vehicle.",
    monthlyPayment: "Estimated monthly payment",
    perMonth: "/ month",
    apr: "APR",
    estimateNotice: "Estimate only. Actual terms depend on lender approval.",
    illustrativeNotice:
      "Uses preset illustrative rates. This estimate is not a loan offer.",
    details: "Calculation details",
    principal: "Amount financed",
    inquiry: "Request loan pre-approval",
    inquiryTitle: "Loan pre-approval request",
    inquiryHelp:
      "Leave your details so our team can help you begin the pre-approval process. This form does not run a credit check or approve a loan. Actual terms are subject to lender review.",
    selectionRequired:
      "Choose a credit range and enter a valid down payment before sending your inquiry.",
    contactRequired:
      "Enter a phone number or email address so we can reach you.",
    contactHint: "Please include a phone number or email address.",
    nameRequired: "Enter your name (at least two characters).",
    emailInvalid: "Enter a valid email address.",
    submit: "Submit financing inquiry",
    received: "Financing inquiry received.",
    thanks:
      "Our team will contact you to discuss your vehicle and financing selections.",
    another: "Send another inquiry",
    selectionSummary: "Your financing selections",
    messagePlaceholder: "Tell us about your financing needs or ask a question.",
  },
  zh: {
    calculator: "购车付款计算器",
    finance: "贷款",
    cash: "全款",
    vehiclePrice: "车辆售价",
    cashPrice: "全款车价",
    cashHelp: "请联系团队确认最终购车总额。",
    excludedFees: "不包含税金、上牌费及文件费。",
    downPayment: "首付",
    downPaymentSlider: "首付滑块",
    downPaymentHelp: "滑块每次调整 $250，也可以直接输入准确金额。",
    downPaymentError: "请输入 $0 至车辆售价之间的首付，最多保留两位小数。",
    loanTerm: "贷款期限",
    months: "个月",
    creditTier: "预估信用分数",
    creditHelp: "选择分数范围后查看估算，此操作不会查询您的信用。",
    credit: {
      excellent: "优秀",
      very_good: "很好",
      good: "良好",
      consultation: "人工咨询",
    },
    lowScore: "619 及以下",
    chooseCredit: "请选择信用分数范围，查看预估月供。",
    loading: "正在加载可用利率…",
    manualReview: "贷款人工咨询",
    manualReviewHelp: "请在下方留下联系方式，团队会与您沟通贷款选择。",
    ratesUnavailable: "此选择暂时没有可用的线上利率。",
    ratesUnavailableHelp: "您仍然可以向团队咨询这辆车的贷款方案。",
    monthlyPayment: "预估月供",
    perMonth: "/ 月",
    apr: "年利率 APR",
    estimateNotice: "结果仅供估算，实际贷款条件以贷款机构审批为准。",
    illustrativeNotice: "使用预设估算利率，此估算并非贷款报价。",
    details: "计算明细",
    principal: "贷款本金",
    inquiry: "预批贷款月供",
    inquiryTitle: "贷款预批请求",
    inquiryHelp:
      "请留下资料，团队会联系您协助办理贷款预批。提交此表格不会查询信用或自动批准贷款，实际贷款条件以贷款机构审核为准。",
    selectionRequired: "请先选择信用分数范围，并输入有效首付，再发送咨询。",
    contactRequired: "请至少填写电话号码或电子邮箱，方便我们联系您。",
    contactHint: "电话号码与电子邮箱至少填写一项。",
    nameRequired: "请输入姓名（至少两个字符）。",
    emailInvalid: "请输入有效的电子邮箱地址。",
    submit: "发送贷款咨询",
    received: "已收到贷款咨询。",
    thanks: "团队会联系您，沟通所选车辆及贷款方案。",
    another: "再次发送咨询",
    selectionSummary: "您选择的贷款信息",
    messagePlaceholder: "告诉我们您的贷款需求，或留下您的问题。",
  },
};

export function financingMoney(
  cents: number,
  locale: Locale = "en",
  decimals = false,
) {
  return new Intl.NumberFormat(locale === "zh" ? "zh-CN" : "en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: decimals ? 2 : 0,
    maximumFractionDigits: decimals ? 2 : 0,
  }).format(cents / 100);
}

export const preapprovalCopy = {
  en: {
    fields: "Pre-approval details",
    name: "Full name *",
    phone: "Phone *",
    email: "Email *",
    ssn: "Social Security number (SSN) *",
    ssnHelp: "Enter your 9-digit SSN. This field is masked for privacy.",
    addressLine1: "Street address *",
    addressLine2: "Apartment / unit (optional)",
    city: "City *",
    state: "State *",
    chooseState: "Select a state",
    postalCode: "ZIP code *",
    residence: "Time at this address",
    residenceYears: "Years *",
    residenceMonths: "Months *",
    consent:
      "I authorize YC Auto to collect and use these details to review my pre-approval request and contact me. I have read the Privacy Policy. This does not authorize a credit check.",
    privacy: "Read the Privacy Policy",
    notice:
      "This form collects a pre-approval request. It does not check your credit, automatically approve a loan, or guarantee a loan offer.",
    requiredContact: "Enter your full name, phone number and email address.",
    invalidFields: "Please check the required application fields below.",
    invalidSsn: "Enter a valid 9-digit SSN.",
    invalidAddress: "Enter your street address, city, state and ZIP code.",
    invalidResidence:
      "Enter whole years from 0 to 100 and months from 0 to 11.",
    consentRequired:
      "Please review and accept the privacy consent before submitting.",
    submit: "Submit pre-approval request",
    received: "Pre-approval request received.",
    thanks:
      "Our team will review your request and contact you. This is not a loan approval. Your application fields have been cleared from this form.",
    another: "Start another request",
    submitError:
      "Your request could not be submitted. Your entries are still here; please try again.",
    adminTitle: "Private pre-approval application",
    adminHelp:
      "Choose a purpose and explicitly request the application. Access is recorded in the audit log.",
    purpose: "Purpose for viewing",
    choosePurpose: "Select a purpose",
    purposes: {
      application_review: "Review application",
      customer_follow_up: "Follow up with customer",
      data_correction: "Check application details",
    },
    view: "View full application (including SSN)",
    viewing: "Loading application…",
    purposeRequired: "Select a purpose before viewing the application.",
    temporary:
      "Private details are cleared after 60 seconds, when this tab is hidden, or when the window loses focus.",
    clear: "Clear private details now",
    cleared:
      "Private details have been cleared. Choose a purpose to view them again.",
    revealSsn: "Show SSN",
    hideSsn: "Hide SSN",
    stored: "Application stored",
    expires: "Expires",
    deleted: "Private application data has been deleted.",
    unavailable: "Private application data is no longer available.",
    delete: "Delete private application data",
    deleteWarning:
      "Permanently delete this applicant’s private application data? This cannot be undone. The lead and access audit remain.",
    confirmDelete: "Confirm deletion",
    cancelDelete: "Cancel",
    deleting: "Deleting…",
    viewError:
      "Unable to load this application. Check your access and try again.",
    deleteError: "Unable to delete the private data. Please try again.",
    submitted: "Submitted",
  },
  zh: {
    fields: "贷款预批资料",
    name: "姓名 *",
    phone: "电话号码 *",
    email: "电子邮箱 *",
    ssn: "社会安全号码（SSN）*",
    ssnHelp: "请输入 9 位 SSN，输入内容会遮盖显示。",
    addressLine1: "街道地址 *",
    addressLine2: "公寓 / 单元号（选填）",
    city: "城市 *",
    state: "州 *",
    chooseState: "请选择州",
    postalCode: "邮政编码 *",
    residence: "在此地址居住时间",
    residenceYears: "年 *",
    residenceMonths: "月 *",
    consent:
      "我同意 YC Auto 收集并使用这些资料来审核我的贷款预批请求及联系我，并已阅读隐私政策。此同意不授权查询信用。",
    privacy: "阅读隐私政策",
    notice:
      "此表格用于收集贷款预批请求，不会查询信用、自动批准贷款或保证贷款报价。",
    requiredContact: "请填写姓名、电话号码和电子邮箱。",
    invalidFields: "请检查下方必填申请资料。",
    invalidSsn: "请输入有效的 9 位 SSN。",
    invalidAddress: "请填写街道地址、城市、州及邮政编码。",
    invalidResidence:
      "居住时间请填写 0 至 100 的整数年数，以及 0 至 11 的整数月数。",
    consentRequired: "请阅读并勾选隐私同意后再提交。",
    submit: "提交贷款预批请求",
    received: "已收到贷款预批请求。",
    thanks:
      "团队会审核您的请求并联系您。这并非贷款批准，申请资料已从此表格清除。",
    another: "填写新的请求",
    submitError: "请求暂时无法提交，已填写的资料仍保留在表单中，请重试。",
    adminTitle: "私密贷款预批申请",
    adminHelp: "请选择查看用途，再主动调取申请资料。访问操作会记入审计日志。",
    purpose: "查看用途",
    choosePurpose: "请选择用途",
    purposes: {
      application_review: "审核申请",
      customer_follow_up: "跟进客户",
      data_correction: "核对资料",
    },
    view: "查看完整申请资料（含 SSN）",
    viewing: "正在加载申请资料…",
    purposeRequired: "请先选择查看用途。",
    temporary: "资料将在 60 秒后、切换至其他标签页或窗口失去焦点时自动清除。",
    clear: "立即清除已显示资料",
    cleared: "已清除显示的私密资料。如需再次查看，请重新选择用途。",
    revealSsn: "显示 SSN",
    hideSsn: "隐藏 SSN",
    stored: "已保存申请",
    expires: "到期时间",
    deleted: "私密申请资料已删除。",
    unavailable: "私密申请资料已不可用。",
    delete: "删除私密申请资料",
    deleteWarning:
      "永久删除此申请人的私密申请资料？此操作无法撤销，线索和访问审计记录会保留。",
    confirmDelete: "确认删除",
    cancelDelete: "取消",
    deleting: "正在删除…",
    viewError: "无法加载申请资料，请检查访问权限后重试。",
    deleteError: "暂时无法删除私密资料，请重试。",
    submitted: "提交时间",
  },
};
